import { ReactElement, useCallback, useMemo } from 'react';
import { Col } from 'reactstrap';

import {
  AxiosResult,
  AxiosResultDefaultError,
} from '../../../../../../api/request';
import { AuthenticationError } from '../../../../../../api/requests/tuitionProcess';
import { createFetchContext } from '../../../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../../../components/info/DisplayError';
import Loading from '../../../../../../components/info/Loading';
import { useParameters } from '../../../../../../hooks/useParameters';
import { IdentificationDocumentCode } from '../../../../../../utils/enums/identification-document-code.enum';
import { searchAdminAccount } from '../services';
import {
  AdminAccountForm,
  IdentificationQuery,
  SearchAdminAccount,
} from '../types';
import {
  adminAccountAdapter,
  adminAccountEmptyAdapter,
} from '../utils/adminAccountAdapter';

interface UserLoaderProps {
  queryParams: IdentificationQuery | null;
  children: (props: {
    user: AdminAccountForm;
    hasQuery: boolean;
    hasPassport: boolean;
    refresh: () => void;
  }) => ReactElement;
}

const { FetchProvider, FetchConsumer } = createFetchContext<
  undefined,
  SearchAdminAccount,
  AuthenticationError | AxiosResultDefaultError
>();

export const UserLoaderConsumer = FetchConsumer;

export default function UserLoader({ children, queryParams }: UserLoaderProps) {
  const request = useCallback(async (): Promise<
    AxiosResult<SearchAdminAccount, AxiosResultDefaultError>
  > => {
    if (!queryParams) {
      return { data: (undefined as unknown) as SearchAdminAccount };
    }
    return searchAdminAccount(queryParams);
  }, [queryParams]);

  const hasQuery = useMemo(() => queryParams !== null, [queryParams]);
  const hasPassport = useMemo(
    () =>
      queryParams?.identificationTypeId === IdentificationDocumentCode.Passport,
    [queryParams],
  );
  const { countryOptions, roleOptions } = useParameters();

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ data, loading, error, refresh }) => {
          if (error) {
            if (error.code === 'HTTP_ERROR' && error.status !== 404) {
              return (
                <Col md={12}>
                  <DisplayError
                    insideCard
                    textBody={error.code}
                    retryAction={refresh}
                    loadingAction={loading}
                  />
                </Col>
              );
            }
          }

          if (loading) {
            return (
              <Col md={12}>
                <div className="mx-3">
                  <Loading insideCard />
                </div>
              </Col>
            );
          }

          return children({
            user:
              !data || error
                ? adminAccountEmptyAdapter(queryParams, countryOptions)
                : adminAccountAdapter(data, queryParams, {
                    countryOptions,
                    roleOptions,
                  }),
            hasQuery,
            hasPassport,
            refresh,
          });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
